import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { LOGIN_PATH } from '@noor/api';
import { subAppForPath, isSharedPath } from '@/entities/sub-app';
import { EatsShell } from '@/widgets/eats-shell';
import { DeliveryShellLayout } from '@/widgets/delivery-shell';

/** Router context: the query client is injected so loaders can prefetch. */
export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

/**
 * Root layout for the merchant super-app. Each sub-app owns its own chrome and
 * lives under its own path namespace (`/eats`, `/delivery`, and future
 * `/pharmacy`, `/shop`, `/buchet`). Which prefix belongs to which sub-app comes
 * from the `entities/sub-app` catalog, so adding one is a catalog edit plus a
 * shell branch here. Full-bleed screens with no sub-app chrome — the public
 * login, the `/welcome` launcher, and the bare `/` redirect target — render the
 * `<Outlet />` directly.
 *
 * `/profile` and `/support` belong to no sub-app: they keep the chrome of
 * whichever one the merchant was last in, so opening support from Доставка
 * doesn't silently move them to Eats.
 */
type ShellSubApp = 'eats' | 'delivery';

/**
 * Full-bleed routes that sit outside every sub-app shell.
 *
 * `/delivery/orders/create` is in here despite living under a sub-app prefix:
 * the old panel routes it through a layout that renders nothing but nested
 * divs — no header, no nav — and the screen itself is `100vw × 100vh`. Order
 * creation is a full-screen map with a floating panel, and the sub-app header
 * would both steal 100px from the map and contradict the reference.
 */
const CHROMELESS_PATHS = [
  LOGIN_PATH,
  '/reset-password',
  '/welcome',
  '/operating-rules',
  '/',
  '/delivery/orders/create',
  '/delivery/create',
];

function isChromelessPath(pathname: string): boolean {
  if (CHROMELESS_PATHS.includes(pathname)) return true;
  if (/^\/delivery\/orders\/[^/]+\/edit$/.test(pathname)) return true;
  if (/^\/(ru|uz|en)\/operating-rules$/.test(pathname)) return true;
  return false;
}

/**
 * Which shell a path maps to. Resolved from the sub-app catalog, then narrowed
 * to the sub-apps that actually have a shell built — a catalog entry flipped to
 * `available` before its chrome exists falls through to a bare outlet rather
 * than crashing.
 */
function shellFor(pathname: string): ShellSubApp | null {
  const id = subAppForPath(pathname)?.id;
  return id === 'eats' || id === 'delivery' ? id : null;
}

/**
 * The sub-app the merchant was last inside. Module-level rather than component
 * state: it must survive the shared route's render without causing one, and a
 * `useRef` written during render is a React correctness violation. Eats is the
 * default because it is the landing sub-app.
 */
let lastSubApp: ShellSubApp = 'eats';

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isChromeless = isChromelessPath(pathname);
  const isShared = isSharedPath(pathname);
  const current = shellFor(pathname);

  // Recorded in an effect, never during render.
  useEffect(() => {
    if (current) lastSubApp = current;
  }, [current]);

  // A shared route keeps the chrome of the sub-app we were last in, so opening
  // support from Доставка doesn't silently move the merchant to Eats.
  const shell = current ?? (isShared ? lastSubApp : null);
  const isDelivery = shell === 'delivery';
  const isEats = shell === 'eats';

  if (isChromeless) {
    return (
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    );
  }

  if (isDelivery) {
    return (
      <DeliveryShellLayout>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </DeliveryShellLayout>
    );
  }

  if (isEats) {
    return (
      <EatsShell>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </EatsShell>
    );
  }

  // Any other sub-app renders its own shell here as it is added; until then an
  // unmatched top-level path falls through to the router's not-found.
  return (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
}
